import { Divider, Stack, Typography, useTheme } from "@mui/material";
import {
	Banknote,
	Box,
	FileText,
	House,
	Info,
	Paperclip,
	ShoppingCart,
} from "lucide-react";
import {
	type FC,
	type PropsWithChildren,
	type ReactNode,
	useLayoutEffect,
	useRef,
	useState,
} from "react";
import {
	Resources,
	useResource,
} from "web_myplann/Infrastructure/Translations/Resources";

export const DataCardPreset = {
	BasicInfo: "basicInfo",
	Documents: "documents",
	RelatedContracts: "relatedContracts",
	RelatedProperties: "relatedProperties",
	RelatedServices: "relatedServices",
	PropertyValue: "propertyValue",
	PropertyParameters: "propertyParameters",
	Value: "value",
} as const;

type PresetProps = {
	preset: (typeof DataCardPreset)[keyof typeof DataCardPreset];
	label?: never;
	icon?: never;
	children?: ReactNode;
	bottomComponent?: ReactNode;
	noDivider?: boolean;
	hideIfEmpty?: boolean;
};

type CustomProps = {
	preset?: never;
	label: string;
	icon: ReactNode;
	children?: ReactNode;
	bottomComponent?: ReactNode;
	noDivider?: boolean;
	hideIfEmpty?: boolean;
};

export type Props = PresetProps | CustomProps;

export const DataCard: FC<PropsWithChildren<Props>> = ({
	label,
	icon,
	preset,
	children,
	bottomComponent,
	noDivider,
	hideIfEmpty = true,
}) => {
	const theme = useTheme();
	const { t } = useResource();

	const childrenRef = useRef<HTMLDivElement | null>(null);
	const bottomComponentRef = useRef<HTMLDivElement | null>(null);

	const [hidden, setHidden] = useState(false);
	const [displayBottomComponent, setDisplayBottomComponent] = useState(true);

	useLayoutEffect(() => {
		if (childrenRef.current) {
			const stackHeight = childrenRef.current.clientHeight;

			const stackPaddings = Number.parseInt(
				getComputedStyle(childrenRef.current).paddingTop,
			);

			if (stackPaddings === stackHeight) {
				setHidden(true);
			}
		}
	}, [children]);

	useLayoutEffect(() => {
		if (bottomComponentRef.current) {
			const stackHeight = bottomComponentRef.current.clientHeight;

			const stackPaddings = Number.parseInt(
				getComputedStyle(bottomComponentRef.current).paddingTop,
			);

			if (stackPaddings === stackHeight) {
				setDisplayBottomComponent(false);
			}
		}
	}, [bottomComponent]);

	if (hideIfEmpty && hidden && !(bottomComponent && displayBottomComponent)) {
		return null;
	}

	let finalLabel = label ?? "";
	let finalIcon = icon ?? null;

	switch (preset) {
		case DataCardPreset.BasicInfo:
			finalLabel = t(Resources.DataCard.BasicInfo.title);
			finalIcon = (
				<Info
					size={theme.iconSize.sm}
					color={theme.palette.deprecated.grey[700]}
				/>
			);
			break;

		case DataCardPreset.Documents:
			finalLabel = t(Resources.DataCard.Documents.title);
			finalIcon = (
				<Paperclip
					size={theme.iconSize.sm}
					color={theme.palette.deprecated.grey[700]}
				/>
			);
			break;

		case DataCardPreset.RelatedContracts:
			finalLabel = t(Resources.DataCard.RelatedContracts.title);
			finalIcon = (
				<FileText
					size={theme.iconSize.sm}
					color={theme.palette.deprecated.grey[700]}
				/>
			);
			break;

		case DataCardPreset.RelatedProperties:
			finalLabel = t(Resources.DataCard.RelatedProperties.title);
			finalIcon = (
				<House
					size={theme.iconSize.sm}
					color={theme.palette.deprecated.grey[700]}
				/>
			);
			break;

		case DataCardPreset.RelatedServices:
			finalLabel = t(Resources.DataCard.RelatedServices.title);
			finalIcon = (
				<ShoppingCart
					size={theme.iconSize.sm}
					color={theme.palette.deprecated.grey[700]}
				/>
			);
			break;

		case DataCardPreset.PropertyValue:
			finalLabel = t(Resources.DataCard.PropertyValue.title);
			finalIcon = (
				<Banknote
					size={theme.iconSize.sm}
					color={theme.palette.deprecated.grey[700]}
				/>
			);
			break;

		case DataCardPreset.PropertyParameters:
			finalLabel = t(Resources.DataCard.PropertyParameters.title);
			finalIcon = (
				<Box
					size={theme.iconSize.sm}
					color={theme.palette.deprecated.grey[700]}
				/>
			);
			break;

		case DataCardPreset.Value:
			finalLabel = t(Resources.DataCard.Value.title);
			finalIcon = (
				<Banknote
					size={theme.iconSize.sm}
					color={theme.palette.deprecated.grey[700]}
				/>
			);
			break;
	}

	return (
		<Stack pb={noDivider ? 2 : 0}>
			<Stack direction="row" alignItems="center" gap={0.8}>
				{finalIcon}
				<Stack>
					<Typography
						variant="deprecatedH3"
						color={theme.palette.deprecated.grey[700]}
					>
						{finalLabel}
					</Typography>
				</Stack>
			</Stack>

			<Stack
				ref={childrenRef}
				pt={1}
				sx={{ display: hidden ? "none" : undefined }}
			>
				{children}
			</Stack>

			{displayBottomComponent && bottomComponent && (
				<Stack pt={1.6}>
					<Typography
						variant="deprecatedBodyStrong"
						color={theme.palette.deprecated.grey[700]}
					>
						{t(Resources.DataCard.BasicInfo.additionalInfo)}
					</Typography>
					<Stack ref={bottomComponentRef} pt={0.8}>
						{bottomComponent}
					</Stack>
				</Stack>
			)}

			{!noDivider && (
				<Stack py={1.2}>
					<Divider sx={{ borderColor: theme.palette.deprecated.grey[200] }} />
				</Stack>
			)}
		</Stack>
	);
};
